// NoteRow.tsx
import React, { useEffect, useRef, useState } from 'react';
import { ILegaltransactionNote } from '../../Interfaces/ILegaltransactionNote';
import {
  IconButton,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  Tooltip,
} from '@mui/material';
import { castWithZeroTimezone } from '../../core/generic_helper';
import { Delete, Edit } from '@mui/icons-material';
import { CustomeTextField } from '../../generic/CustomeTextField';
import { useGetUsers } from '../../hooks/data';
import { showConfirmDialogComponent } from '../../core/ConfirmDialog';
import ReplayIcon from '@mui/icons-material/Replay';


interface IProps {
  note: ILegaltransactionNote;
  setNoteArray: React.Dispatch<React.SetStateAction<ILegaltransactionNote[]>>;
}

export const NoteRow: React.FC<IProps> = ({ note, setNoteArray }) => {
  const { users } = useGetUsers();
  const theme = useTheme();

  const idUser = Number(localStorage.getItem('idUser'));
  const currentUser = users?.find(x => x.idUser === idUser);

  const isAdminOrNotary =
    currentUser !== undefined &&
    (Boolean(currentUser.isNotary) || currentUser.idRole === 1);

  const isUser = currentUser?.idUser === note.idUser;
  const havePermissionToEditNote = isAdminOrNotary || isUser;

  const [isEditing, setIsEditing] = useState<boolean>(
    note.idLegalTransactionNote < 0
  );
  const [editNote, setEditNote] = useState<ILegaltransactionNote>(note);

  const originalNoteRef = useRef<ILegaltransactionNote>(note);

  useEffect(() => {
    if (!isEditing) {
      setEditNote(note);
      originalNoteRef.current = note;
    }
  }, [note, isEditing]);

  const upsertToParent = (next: ILegaltransactionNote) => {
    setNoteArray(prev => {
      const idx = prev.findIndex(
        x => x.idLegalTransactionNote === next.idLegalTransactionNote
      );

      if (idx === -1) {
        return [next, ...prev];
      }

      const copy = [...prev];
      copy[idx] = next;
      return copy;
    });
  };

  const onRemove = async () => {
    const confirmed = await showConfirmDialogComponent(
      'Warnung',
      'Sind Sie sicher, dass Sie die Notiz löschen wollen?',
      'Löschen',
      'Abbrechen',
      theme
    );

    if (!confirmed) return;

    setNoteArray(prev =>
      prev.filter(x => x.idLegalTransactionNote !== note.idLegalTransactionNote)
    );
  };

  const onEdit = () => {
    originalNoteRef.current = editNote;
    setIsEditing(true);
  };

  const onCancelEdit = () => {
    const reset = originalNoteRef.current;
    setEditNote(reset);
    upsertToParent(reset);
    setIsEditing(false);
  };

  const onChangeDraft = (next: ILegaltransactionNote) => {
    const isExisting = next.idLegalTransactionNote > 0;

    const wasChanged = isExisting && next.Note !== originalNoteRef.current.Note;

    const normalized: ILegaltransactionNote = {
      ...next,
      isEdited: isExisting
        ? wasChanged
          ? true
          : Boolean(next.isEdited)
        : false,
    };

    setEditNote(normalized);
    upsertToParent(normalized);
  };

  return (
    <TableRow>
      <TableCell>
        {castWithZeroTimezone(String(note.Created_at)).toLocaleString()}
      </TableCell>

      <TableCell>
        {isUser ? 'Ich' : `${note.FirstName ?? ''} ${note.LastName ?? ''}`}
      </TableCell>

      <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
        {isEditing ? (
          <CustomeTextField
            label='Notiz'
            attr='Note'
            object={editNote}
            setObject={onChangeDraft}
            type='string'
            rows={3}
          />
        ) : (
          note.Note
        )}
      </TableCell>

      <TableCell>
        {note.idLegalTransactionNote > 0 && Boolean(note.isEdited) ? (
          <Typography variant='caption'>(Bearbeitet)</Typography>
        ) : null}
      </TableCell>

      <TableCell>
        {havePermissionToEditNote ? (
          !isEditing ? (
            <>
              <Tooltip title='Notiz bearbeiten' arrow>
                <IconButton onClick={onEdit}>
                  <Edit />
                </IconButton>
              </Tooltip>

              <Tooltip title='Notiz löschen' arrow>
                <IconButton onClick={onRemove}>
                  <Delete />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip
              title='Änderungen auf ursprüngliche Notiz zurücksetzen'
              arrow
            >
              <IconButton onClick={onCancelEdit}>
                <ReplayIcon />
              </IconButton>
            </Tooltip>
          )
        ) : null}
      </TableCell>
    </TableRow>
  );
};
